import { Injectable } from '@angular/core';
import { Project } from 'src/app/shared/models/project';
import { Constants } from 'src/app/shared/utils/constants';
import { ApplicationDateSurfaceWater } from 'src/app/shared/models/application-date';
import { ApplicationDateApiService } from 'src/app/shared/services/application-date.api.service';
import { Observable, map, shareReplay } from 'rxjs';

export interface Swash63ApplicationWindow extends ApplicationDateSurfaceWater {
  bbchStart?: string;
  bbchEnd?: string;
  bbchStartDate?: string;
  bbchEndDate?: string;
  allocatedBbchOptions?: { value: string }[];
  rangeIndex?: number;
  noBbch?: boolean;
}

interface Swash63BbchRange {
  applicationDateSurfaceWaterPk?: number;
  location?: string;
  season?: number;
  bbchStart?: string;
  bbchEnd?: string;
}

const EMERGENCE_BBCH = '09';
const HARVEST_BBCH = '90';
const NO_BBCH = Constants.SWASH63_NO_BBCH;
const NO_BBCH_START_DATE = '1999-01-01T00:00:00';
const NO_BBCH_END_DATE = '1999-12-31T00:00:00';
const RUNOFF_SCENARIO_PREFIX = 'R';
const GAP_CROPS_WITHOUT_BBCH = [
  Constants.GAP_CROPS_FOR_APP_DATES.CITRUS,
  Constants.GAP_CROPS_FOR_APP_DATES.GRASS_ALFALFA,
  Constants.GAP_CROPS_FOR_APP_DATES.OLIVES,
];
const MIN_INTERCEPTION = 0;
const MAX_INTERCEPTION = 100;
const INTERCEPTION_TOLERANCE = 1e-6;

export type Swash63ValidationField = 'cropAppDatePk' | 'bbchEarliest' | 'bbchLatest' | 'applicationDate' |
  'bbchStart' | 'bbchEnd' | 'beginWindow';

export interface Swash63ApplicationDate {
  applicationNumber: number;
  applicationDate: string;
  bbch: string;
  warnings: string[];
}

export interface Swash63RunDates {
  startDate: string;
  endDate: string;
  bbchStart: string;
  bbchEnd: string;
  emergenceDate?: string;
  harvestDate?: string;
  applications: Swash63ApplicationDate[];
}

@Injectable({
  providedIn: 'root',
})
export class EEAGapSwash63LogicService {
  private readonly appDates = new Map<number, Observable<ApplicationDateSurfaceWater[]>>();
  private readonly validationWarnings = new WeakMap<object, Partial<Record<Swash63ValidationField, string[]>>>();
  private readonly rowsWithoutBbch = new WeakSet<object>();

  constructor(private applicationDateApiService: ApplicationDateApiService) {}

  getApplicationDates(
    cropListPk: number, bbchStart: string, bbchEnd?: string, ranges: Swash63BbchRange[] = [], cropName?: string,
  ): Observable<Swash63ApplicationWindow[]> {
    return this.getCropApplicationDates(cropListPk)
      .pipe(map(dates => this.calculateScenarioDates(dates, bbchStart, bbchEnd, ranges, cropName)));
  }

  getAllocatedBbch(
    cropListPk: number, bbchStart: string, bbchEnd?: string, cropName?: string,
  ): Observable<{ allocatedBbchStart: string | null; allocatedBbchEnd: string | null }> {
    return this.getCropApplicationDates(cropListPk).pipe(map(dates => {
      if (this.isCropWithoutBbch(cropName)) {
        return { allocatedBbchStart: null, allocatedBbchEnd: null };
      }
      const allocatedFor = (bbch?: string): string | null => {
        if (bbch == null || bbch.trim() === '') return null;
        const values = dates
          .filter(date => date.bbch === Number(bbch))
          .map(date => date.allocatedBbch)
          .filter((value): value is string => value != null && value !== '');
        return [...new Set(values)].join(', ') || null;
      };
      return { allocatedBbchStart: allocatedFor(bbchStart), allocatedBbchEnd: allocatedFor(bbchEnd) };
    }));
  }

  getCropHasBbchData(cropListPk: number, cropName?: string): Observable<boolean> {
    return this.getCropApplicationDates(cropListPk).pipe(
      map(dates => !this.isCropWithoutBbch(cropName) &&
        dates.some(date => date.bbch != null && Number.isFinite(Number(date.bbch)))),
    );
  }

  getRunApplicationDates(applicationScheme: any, intervals: number[]): Observable<Swash63RunDates> {
    return this.getCropApplicationDates(applicationScheme.cropAppDatePk)
      .pipe(map(dates => this.calculateRunApplicationDates(applicationScheme, intervals, dates)));
  }

  calculateRunApplicationDates(
    applicationScheme: any, intervals: number[], dates: ApplicationDateSurfaceWater[],
  ): Swash63RunDates {
    const numberOfApplications = Math.max(Number(applicationScheme.numberOfApplications) || 1, 1);
    const isBackward = applicationScheme.appTimingDirection === Constants.APP_TIMING_DIRECTIONS.BACKWARD;
    const offsets = [0];
    intervals.slice(0, numberOfApplications - 1).forEach(interval => offsets.push(offsets[offsets.length - 1] + interval));
    while (offsets.length < numberOfApplications) offsets.push(offsets[offsets.length - 1]);
    const totalInterval = offsets[offsets.length - 1];
    const firstDay = isBackward
      ? this.toDay(applicationScheme.endDate) - totalInterval
      : this.toDay(applicationScheme.firstDate);

    const records = dates
      .filter(date => date.location === applicationScheme.location && Number(date.season) === Number(applicationScheme.season))
      .filter(date => !!date.applicationDate)
      .sort((a, b) => this.toDay(a.applicationDate) - this.toDay(b.applicationDate));
    const hasBbch = records.some(date => date.bbch != null && Number.isFinite(Number(date.bbch)));
    const bbchFor = (day: number): string => {
      if (!hasBbch) return NO_BBCH;
      const record = [...records].reverse().find(date => this.toDay(date.applicationDate) <= day) ?? records[0];
      return record?.allocatedBbch || String(record?.bbch ?? '').padStart(2, '0');
    };
    const recordDate = (bbch: string) => records.find(date => Number(date.bbch) === Number(bbch))?.applicationDate ?? undefined;
    const emergenceDate = applicationScheme.emergencyDate ?? (hasBbch ? recordDate(EMERGENCE_BBCH) : undefined);
    const harvestDate = applicationScheme.harvestDate ?? (hasBbch ? recordDate(HARVEST_BBCH) : undefined);

    const applications = offsets.map((offset, index) => {
      const day = firstDay + offset;
      const warnings: string[] = [];
      if (hasBbch && emergenceDate && day < this.toDay(emergenceDate)) warnings.push('Application Date is before the Emergence date.');
      if (hasBbch && harvestDate && day > this.toDay(harvestDate)) warnings.push('Application Date is after the Harvest date.');
      return { applicationNumber: index + 1, applicationDate: this.fromDay(day), bbch: bbchFor(day), warnings };
    });
    return {
      startDate: applications[0].applicationDate,
      endDate: applications[applications.length - 1].applicationDate,
      bbchStart: applications[0].bbch,
      bbchEnd: applications[applications.length - 1].bbch,
      emergenceDate: hasBbch ? emergenceDate : undefined,
      harvestDate: hasBbch ? harvestDate : undefined,
      applications,
    };
  }

  private toDay(value: string | Date | null | undefined): number {
    const text = value instanceof Date ? value.toISOString() : String(value ?? '');
    const [year, month, day] = text.substring(0, 10).split('-').map(Number);
    return Math.round(Date.UTC(year, month - 1, day) / 86400000);
  }

  private fromDay(day: number): string {
    return `${new Date(day * 86400000).toISOString().substring(0, 10)}T00:00:00`;
  }

  private getCropApplicationDates(cropListPk: number): Observable<ApplicationDateSurfaceWater[]> {
    if (!this.appDates.has(cropListPk)) {
      this.appDates.set(cropListPk, this.applicationDateApiService
        .getApplicationDatesSurfaceWaterByVersion(Constants.APP_DATE_VERSION_SWASH63, cropListPk)
        .pipe(shareReplay({ bufferSize: 1, refCount: false })));
    }
    return this.appDates.get(cropListPk)!;
  }

  calculateScenarioDates(
    dates: ApplicationDateSurfaceWater[], bbchStart: string, bbchEnd?: string,
    ranges: Swash63BbchRange[] = [], cropName?: string,
  ): Swash63ApplicationWindow[] {
    const scenarioLocations = this.getNoBbchScenarioLocations(cropName);
    const matchingScenarioDates = scenarioLocations
      ? dates.filter(date => scenarioLocations.includes(date.location))
      : [];
    const scenarioDates = scenarioLocations && matchingScenarioDates.length > 0
      ? matchingScenarioDates
      : dates;
    const scenarioKey = (location?: string, season?: number) => `${location}|${season}`;
    const datesByPk = new Map(scenarioDates.map(date => [date.applicationDateSurfaceWaterPk, date]));
    const rangeKeys = ranges.map(range => {
      const date = range.applicationDateSurfaceWaterPk != null ? datesByPk.get(range.applicationDateSurfaceWaterPk) : undefined;
      if (date) return scenarioKey(date.location, date.season);
      return range.season != null ? scenarioKey(range.location, range.season) : undefined;
    });
    const usedRanges = new Set<number>();
    const findRangeIndex = (location: string, season: number): number => {
      const key = scenarioKey(location, season);
      let index = rangeKeys.findIndex((rangeKey, i) => !usedRanges.has(i) && rangeKey === key);
      if (index < 0) {
        index = ranges.findIndex((range, i) => !usedRanges.has(i) && !rangeKeys[i] && range.location === location);
      }
      if (index >= 0) usedRanges.add(index);
      return index;
    };

    const scenarios = new Map<string, ApplicationDateSurfaceWater[]>();
    scenarioDates.forEach(date => {
      const key = scenarioKey(date.location, date.season);
      scenarios.set(key, [...(scenarios.get(key) ?? []), date]);
    });

    return [...scenarios.values()]
      .sort((a, b) => a[0].location.localeCompare(b[0].location) || a[0].season - b[0].season)
      .map(records => {
        const { location, season } = records[0];
        const rangeIndex = findRangeIndex(location, season);
        const range = rangeIndex >= 0 ? ranges[rangeIndex] : undefined;
        const isEmpty = (bbch?: string | null): boolean => bbch == null || bbch.trim() === '';
        const recordFor = (bbch?: string) =>
          isEmpty(bbch) ? undefined : records.find(date => date.bbch === Number(bbch));
        const recordForAllocated = (bbch?: string) => isEmpty(bbch) ? undefined :
          records.find(date => !isEmpty(date.allocatedBbch) && Number(date.allocatedBbch) === Number(bbch)) ?? recordFor(bbch);
        const hasStartOverride = range?.bbchStart != null;
        const hasEndOverride = range?.bbchEnd != null;
        const startRecord = hasStartOverride ? recordForAllocated(range!.bbchStart) : recordFor(bbchStart);
        const endRecord = hasEndOverride ? recordForAllocated(range!.bbchEnd) : recordFor(bbchEnd);
        const start = hasStartOverride ? range!.bbchStart : bbchStart;
        const end = hasEndOverride ? range!.bbchEnd : bbchEnd;
        const hasBbchData = records.some(date => Number.isFinite(date.bbch));
        const noBbch = !!scenarioLocations || (isEmpty(start)
          ? !hasBbchData
          : isEmpty(startRecord?.applicationDate) || (!isEmpty(end) && isEmpty(endRecord?.applicationDate)));
        const allocated = (record: ApplicationDateSurfaceWater | undefined, bbch?: string): string | undefined =>
          isEmpty(record?.allocatedBbch) ? (isEmpty(bbch) ? undefined : bbch) : record!.allocatedBbch!;
        const dateFor = (bbch?: string): string | undefined => recordFor(bbch)?.applicationDate ?? undefined;
        const scenario = recordFor(bbchStart) ?? records[0];
        const allocatedBbchOptions = [...new Set(records
          .map(date => date.allocatedBbch)
          .filter((value): value is string => !isEmpty(value)))]
          .sort((a, b) => Number(a) - Number(b))
          .map(value => ({ value }));
        return {
          ...scenario,
          bbchStart: noBbch ? NO_BBCH : allocated(startRecord, start),
          bbchEnd: noBbch ? NO_BBCH : allocated(endRecord, end),
          noBbch,
          rangeIndex,
          allocatedBbchOptions,
          beginWindow: noBbch ? NO_BBCH_START_DATE : startRecord?.applicationDate ?? undefined,
          endWindow: noBbch ? NO_BBCH_END_DATE : endRecord?.applicationDate ?? undefined,
          bbchStartDate: startRecord?.applicationDate ?? undefined,
          bbchEndDate: endRecord?.applicationDate ?? undefined,
          emergencyDate: noBbch ? NO_BBCH_START_DATE : dateFor(EMERGENCE_BBCH),
          harvestDate: noBbch ? NO_BBCH_END_DATE : dateFor(HARVEST_BBCH),
        };
      });
  }

  private isCropWithoutBbch(cropName?: string): boolean {
    return !!cropName && GAP_CROPS_WITHOUT_BBCH.some(crop => crop.toLowerCase() === cropName.trim().toLowerCase());
  }

  private getNoBbchScenarioLocations(cropName?: string): string[] | undefined {
    const normalizedCropName = cropName?.trim().toLowerCase();
    if (!normalizedCropName || !this.isCropWithoutBbch(cropName)) return undefined;
    const cropKey = Object.keys(Constants.GAP_CROP_SCENARIOS_WITHOUT_APP_DATES)
      .find(crop => crop.toLowerCase() === normalizedCropName);
    return cropKey ? Constants.GAP_CROP_SCENARIOS_WITHOUT_APP_DATES[cropKey] : undefined;
  }

  getDateWarnings(applicationDate: string | Date | null | undefined, scenario: Swash63ApplicationWindow): string[] {
    return [...new Set(Object.values(this.getFieldDateWarnings(applicationDate, scenario)).flat())];
  }

  private getFieldDateWarnings(
    applicationDate: string | Date | null | undefined, scenario: Swash63ApplicationWindow,
  ): Record<'bbchStart' | 'bbchEnd' | 'beginWindow', string[]> {
    const warnings: Record<'bbchStart' | 'bbchEnd' | 'beginWindow', string[]> = {
      bbchStart: [], bbchEnd: [], beginWindow: [],
    };
    if (!applicationDate) return warnings;
    const day = (value: string | Date): number => {
      const date = new Date(value);
      return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    };
    const date = day(applicationDate);
    if (Number.isNaN(date)) {
      warnings.beginWindow.push('Application Date is invalid.');
      return warnings;
    }
    if (scenario.bbchStartDate && date < day(scenario.bbchStartDate)) warnings.beginWindow.push('Application Date is before BBCH start.');
    if (scenario.bbchEndDate && date > day(scenario.bbchEndDate)) warnings.beginWindow.push('Application Date is after BBCH end.');
    if (scenario.emergencyDate && date < day(scenario.emergencyDate)) warnings.beginWindow.push('Application Date is before the Emergence date.');
    if (scenario.harvestDate && date > day(scenario.harvestDate)) warnings.beginWindow.push('Application Date is after the Harvest date.');
    return warnings;
  }

  clearValidationWarnings(row: object): void {
    this.validationWarnings.delete(row);
  }

  setFieldWarnings(row: object, field: Swash63ValidationField, warnings: string[]): void {
    const fields = this.validationWarnings.get(row) ?? {};
    fields[field] = [...new Set(warnings)];
    this.validationWarnings.set(row, fields);
  }

  getFieldWarning(row: object | undefined, field: Swash63ValidationField): string {
    return row ? this.validationWarnings.get(row)?.[field]?.join('\n') ?? '' : '';
  }

  setRowWithoutBbch(row: object, withoutBbch: boolean): void {
    if (withoutBbch) this.rowsWithoutBbch.add(row);
    else this.rowsWithoutBbch.delete(row);
  }

  isRowWithoutBbch(row: object | undefined): boolean {
    return !!row && this.rowsWithoutBbch.has(row);
  }

  updateScenarioWarnings(row: Swash63ApplicationWindow): void {
    this.clearValidationWarnings(row);
    const warnings = this.getFieldDateWarnings(row.beginWindow, row);
    this.setFieldWarnings(row, 'bbchStart', warnings.bbchStart);
    this.setFieldWarnings(row, 'bbchEnd', warnings.bbchEnd);
    this.setFieldWarnings(row, 'beginWindow', warnings.beginWindow);
  }

  updateApplicationWarnings(row: object, scenarios: Swash63ApplicationWindow[]): void {
    this.clearValidationWarnings(row);
    const warnings = scenarios.map(scenario => this.getFieldDateWarnings(scenario.beginWindow, scenario));
    this.setFieldWarnings(row, 'bbchEarliest', warnings.flatMap(value => value.bbchStart));
    this.setFieldWarnings(row, 'bbchLatest', warnings.flatMap(value => value.bbchEnd));
    this.setFieldWarnings(row, 'applicationDate', warnings.flatMap(value => value.beginWindow));
  }

  isPrzmInputEnabled(row: any): boolean {
    const windows: { location?: string }[] = row?.applicationSchemeXApplicationWindow ?? [];
    return windows.some(window => window?.location?.trim().toUpperCase().startsWith(RUNOFF_SCENARIO_PREFIX) ?? false);
  }

  getInterceptionWarnings(row: any): string[] {
    return this.getInterceptionWarningsForValues(row, 'swashCropInterception', 'cropInterception');
  }

  getDetailInterceptionWarnings(row: any): string[] {
    return this.getInterceptionWarningsForValues(row, 'crop_interception', 'defaultCropInterception');
  }

  private getInterceptionWarningsForValues(row: any, valueField: string, defaultField: string): string[] {
    const value = row?.[valueField];
    if (value == null || value === '') return [];
    const interception = Number(value);
    const warnings: string[] = [];
    if (Number.isNaN(interception) || interception < MIN_INTERCEPTION || interception > MAX_INTERCEPTION) {
      warnings.push(`Interception must be between ${MIN_INTERCEPTION} and ${MAX_INTERCEPTION}%.`);
    }
    const defaultValue = row?.[defaultField];
    const defaultInterception = defaultValue == null || defaultValue === '' ? NaN : Number(defaultValue);
    if (!Number.isNaN(defaultInterception) && !Number.isNaN(interception) &&
      Math.abs(interception - defaultInterception) > INTERCEPTION_TOLERANCE) {
      warnings.push(`Interception was changed from the automatically populated value (${Number(defaultInterception.toFixed(6))}%).`);
    }
    return warnings;
  }

  getCommonApplicationDate(windows: { firstDate?: string | Date | null }[]): string | Date | null {
    if (!windows.length || !windows[0].firstDate) return null;
    const firstDate = new Date(windows[0].firstDate).getTime();
    return windows.every(window => !!window.firstDate && new Date(window.firstDate).getTime() === firstDate)
      ? windows[0].firstDate : null;
  }

  hasSwash63(project?: Project): boolean {
    return !!project?.projectXCompoundXModel?.some(model => model?.ModelName === Constants.MODELS.SWASH63);
  }

  getNumberOfApplicationsOptions(project: Project | undefined, standardOptions: any[]): any[] {
    if (!this.hasSwash63(project)) return standardOptions;

    return Array.from(
      { length: Constants.MAX_NUMBER_OF_APPLICATIONS_SWASH63 },
      (_, index) => ({ value: index + 1 }),
    );
  }

  setApplicationSchemeDefaults(row: any, project?: Project): void {
    if (!this.hasSwash63(project)) return;

    row.appTimingDirection ??= Constants.APP_TIMING_DIRECTIONS.FORWARD;
    row.isPhiApplicable ??= false;
  }

  synchronizeVariableApplicationFlag(
    row: any,
    isInverseModeling: boolean,
    compartment: string | null,
    project?: Project,
  ): void {
    if (isInverseModeling || compartment !== Constants.COMPARTMENTS.SURFACE_WATER || !this.hasSwash63(project)) return;

    row.hasVariableRates = row.numberOfApplications >= 2;
  }

  shouldHideVariableRatesColumn(isInverseModeling: boolean, models: string[]): boolean {
    return !isInverseModeling && models.includes(Constants.MODELS.SWASH63);
  }
}
